import { useCallback, useMemo, useState } from "react";
import {
    selectImagesByPhotoFolder,
    selectInspectionIsDisabled
} from "../../store/selectors/inspectionSelectors";
import { Button } from "primereact/button";
import { ImageGallery } from "./ImageGallery";
import {
    parseItemImages,
    mapImageToItemAsset,
    sortImagesBySelected
} from "../../utils/imageHelper";
import { ItemAsset, ItemWithImages } from "../../types/asset";
import { Dialog } from "primereact/dialog";
import { useAppSelector } from "../../hooks/storeHooks";
import { ImageFolderChips } from "./ImageFolderChips";

type Props<T> = {
    item: T;
    onHide: () => void;
    onSave: (item: T) => Promise<void>;
};

export const ImagesDialog = <T extends ItemWithImages>(props: Props<T>) => {
    const { item, onHide, onSave } = props;

    const inspectionDisabled = useAppSelector(selectInspectionIsDisabled);

    const [enabled, setEnabled] = useState(item.photoFolder);

    const images = selectImagesByPhotoFolder(enabled);

    const parsedImages = useMemo(() => parseItemImages(item.images), [item]);
    const sortedImages = useMemo(
        () => sortImagesBySelected(images, parsedImages),
        [images, parsedImages]
    );

    const [selected, setSelected] = useState<ItemAsset[]>(parsedImages);
    const [loading, setLoading] = useState(false);
    const [selectAll, setSelectAll] = useState(false);

    const onSubmit = useCallback(async () => {
        setLoading(true);

        await onSave({
            ...item,
            images: JSON.stringify(selected)
        });

        setLoading(false);
    }, [item, selected]);

    const onSelectAll = useCallback(() => {
        if (selectAll) {
            setSelected([]);
            setSelectAll(false);
        } else {
            setSelected(sortedImages.map(mapImageToItemAsset));
            setSelectAll(true);
        }
    }, [selectAll]);

    const headerContent = useMemo(
        () => (
            <div>
                <span className="font-bold white-space-nowrap">
                    Edit Images
                </span>
                <ImageFolderChips
                    folders={item.photoFolder}
                    enabled={enabled}
                    setEnabled={setEnabled}
                />
            </div>
        ),
        [item, enabled]
    );

    const footerContent = useMemo(() => {
        if (inspectionDisabled) {
            return null;
        }

        return (
            <div className="flex justify-content-between pt-4">
                <Button
                    severity="secondary"
                    outlined
                    label="Select All"
                    onClick={onSelectAll}
                    disabled={loading}
                />
                <Button
                    label={`Assign ${selected.length} ${
                        selected.length === 1 ? "image" : "images"
                    }`}
                    icon={loading ? "pi pi-spin pi-spinner" : undefined}
                    onClick={onSubmit}
                    disabled={loading}
                />
            </div>
        );
    }, [loading, selected]);

    return (
        <Dialog
            maximized
            visible={true}
            className="full-dialog"
            draggable={false}
            onHide={onHide}
            header={headerContent}
            footer={footerContent}
        >
            <ImageGallery
                disabled={inspectionDisabled}
                images={sortedImages}
                selected={selected}
                setSelected={setSelected}
            />
        </Dialog>
    );
};
